import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, Cloud, Cpu, Target } from "@phosphor-icons/react";

const steps = [
  {
    n: "01",
    icon: Compass,
    title: "Domain Expertise",
    subtitle: "Deep context tailored to complex industrial realities.",
    body: "We start with your operation — not a template. Industry playbooks across metals, mining, manufacturing, construction, realty, education, and agriculture ensure that every model, workflow, and integration is grounded in reality.",
    outcomes: [
      "Industry-specific process discovery",
      "Operational KPI baselining",
      "Executive alignment on outcomes",
      "Value stream mapping",
    ],
    tint: "bg-sky-50",
    accent: "bg-sky-500",
    text: "text-sky-700",
    border: "border-sky-500",
  },
  {
    n: "02",
    icon: Cloud,
    title: "Cloud-Native Foundation",
    subtitle: "Laying a cloud-native foundation for unified data integration.",
    body: "Move from siloed systems to a unified, secure, scalable backbone. Data lakes, modern data platforms, and hybrid cloud architectures replace fragmented spreadsheets and legacy connectors.",
    outcomes: [
      "Cloud migration & hybrid architecture",
      "Unified data lake",
      "Modern data platform",
      "Managed cloud services",
    ],
    tint: "bg-sky-50",
    accent: "bg-sky-600",
    text: "text-sky-700",
    border: "border-sky-600",
  },
  {
    n: "03",
    icon: Cpu,
    title: "AI-First Engine",
    subtitle: "Applying an AI-first engine to unlock predictive and generative capabilities.",
    body: "Predictive maintenance, computer vision, generative AI copilots, and root-cause analysis engines embedded into daily workflows — not bolted on as dashboards.",
    outcomes: [
      "Predictive analytics & maintenance",
      "Computer vision & video analytics",
      "NLP & document intelligence",
      "Generative AI copilots",
    ],
    tint: "bg-blue-50",
    accent: "bg-blue-500",
    text: "text-blue-700",
    border: "border-blue-500",
  },
  {
    n: "04",
    icon: Target,
    title: "Measurable Outcomes",
    subtitle: "Delivering measurable business outcomes and operational ROI.",
    body: "Every deployment ties back to a business metric — efficiency, cost, safety, throughput, decision speed. We instrument outcomes from day one and report against them continuously.",
    outcomes: [
      "30% operational efficiency uplift",
      "50+ AI use cases deployed",
      "100+ processes digitized",
      "24×7 intelligent operations",
    ],
    tint: "bg-sky-50",
    accent: "bg-sky-400",
    text: "text-sky-700",
    border: "border-sky-400",
  },
];

export default function Blueprint() {
  return (
    <div data-testid="page-blueprint">
      {/* HERO */}
      <section className="border-b border-zinc-200" data-testid="blueprint-hero">
        <div className="container-x mx-auto py-20 lg:py-28">
          <div className="text-overline mb-6">The Transformation Blueprint</div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-5xl">
            A proven methodology<br />for industrial intelligence.
          </h1>
          <p className="mt-8 text-xl leading-relaxed text-zinc-600 max-w-3xl">
            Four disciplined steps. One blueprint. From deep domain context to measurable business
            ROI — the path modern operations take to become intelligent enterprises.
          </p>
        </div>
      </section>

      {/* STEPS */}
      <section className="border-b border-zinc-200" data-testid="blueprint-steps">
        <div className="container-x mx-auto py-20 lg:py-28">
          <div className="relative">
            {/* Vertical spine */}
            <div className="hidden lg:block absolute left-[calc(4rem+2px)] top-4 bottom-4 w-px bg-zinc-200" aria-hidden />

            <div className="space-y-16 lg:space-y-24">
              {steps.map((s, i) => (
                <div
                  key={s.n}
                  data-testid={`blueprint-step-${s.n}`}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start"
                >
                  <div className="lg:col-span-3 flex lg:flex-col items-start gap-4 lg:gap-6">
                    <div className="relative shrink-0">
                      <div className={`w-20 h-20 ${s.accent} text-white flex items-center justify-center font-black text-2xl shadow-lg`} style={{ fontFamily: "Chivo" }}>
                        {s.n}
                      </div>
                      <div className={`absolute -right-3 -bottom-3 bg-white border-4 ${s.border} w-10 h-10 flex items-center justify-center shadow-md`}>
                        <s.icon size={18} weight="light" className={s.text} />
                      </div>
                    </div>
                    <div>
                      <div className={`text-overline ${s.text}`}>Step {s.n}</div>
                      <div className="text-2xl font-black tracking-tighter mt-1" style={{ fontFamily: "Chivo" }}>
                        {s.title}
                      </div>
                    </div>
                  </div>

                  <div className={`lg:col-span-9 ${s.tint} border-l-4 ${s.border} pl-8 lg:pl-12 py-6 pr-6`}>
                    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight max-w-3xl">
                      {s.subtitle}
                    </h2>
                    <p className="mt-6 text-lg text-zinc-700 leading-relaxed max-w-3xl">{s.body}</p>

                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl">
                      {s.outcomes.map((o) => (
                        <div key={o} className="bg-white p-4 text-sm font-semibold text-zinc-900 flex items-center gap-3 border border-zinc-200 shadow-sm">
                          <span className={`w-2 h-2 ${s.accent} rounded-full shrink-0`} />
                          {o}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-zinc-200" data-testid="blueprint-cta">
        <div className="container-x mx-auto py-20 lg:py-28">
          <div className="border border-sky-500 p-10 lg:p-16 bg-sky-50/30">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
              <h2 className="lg:col-span-8 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
                Ready to run the blueprint on your operation?
              </h2>
              <div className="lg:col-span-4 flex flex-col gap-3">
                <Link
                  to="/contact"
                  data-testid="blueprint-cta-btn"
                  className="inline-flex items-center justify-between bg-sky-500 text-white px-6 py-4 text-sm font-semibold hover:bg-sky-600 transition-colors"
                >
                  Book a Consultation <ArrowUpRight size={16} weight="bold" />
                </Link>
                <Link
                  to="/architecture"
                  data-testid="blueprint-architecture-btn"
                  className="inline-flex items-center justify-between border border-sky-500 text-sky-700 px-6 py-4 text-sm font-semibold hover:bg-sky-500 hover:text-white transition-colors"
                >
                  Explore the Architecture <ArrowUpRight size={16} weight="bold" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
